/**
 * The Gaps tab, laid out like the redesign's "Checks" (DOP-623): the items
 * that need a person on the left, with how far through them you are, and ONE
 * item at a time on the right, so the question being answered has the room.
 *
 *  - blocks / not found: "I know the answer" (a value and where it came from)
 *    or "Ask the company" — which keeps "Not found in provided materials" in
 *    the memo and the question in the diligence list, and only lifts the
 *    block. Beside it, where the run looked;
 *  - conflict: each value a card side by side with where it was read, and a
 *    Use button each; or "Neither is right" — a figure from elsewhere with
 *    where it came from; or keep both flagged;
 *  - omitted: stays omitted — the section is not filled from general
 *    knowledge, and its question is already in the diligence list.
 *
 * The tab opens on the first item a person can act on — the blocking one
 * first — and after each save it moves to the next one still open, so the
 * items are worked through rather than hunted for. The list is numbered and
 * Previous / "Skip to next" sit at the bottom, after the decision, with
 * "Item 2 of 4" between them, so it is plain there is more than one;
 * stepping records nothing.
 *
 * Below, "Your decisions" (DOP-577): every slot a person decided on — a
 * source chosen, a value entered, "Not found" accepted — with Change, Undo
 * and the history with "Revert to this". A decision made on an earlier
 * version that the materials have since outgrown is marked "review" and
 * listed above as a Review choice item. The memo's Edit links land here too,
 * via `editTarget`.
 *
 * A figure needed for each year that the run found for none (EBITDA across
 * the years) is not answered here with one value: its card opens the memo in
 * edit mode on that row of the financial summary (DOP-650).
 */

import { ChevronLeft, ChevronRight, CircleCheck, FilePlus, Search } from "lucide-react";
import React, { useState } from "react";

import type {
	DealScreeningClearFactPayload,
	DealScreeningDecision,
	DealScreeningFact,
	DealScreeningResolveConflictPayload,
	DealScreeningRun,
	DealScreeningSetFactPayload,
	DealScreeningSource,
} from "../../types/dealScreening";
import { BTN, BTN_SM, CARD, KICKER, KIND_KICKER } from "./checkStyles";
import ConflictChooser from "./ConflictChooser";
import { type ReviewItem, needsEachYear, reviewItems } from "./dealScreeningReview";
import EnterWithSourceCard from "./EnterWithSourceCard";
import StaleEntryReview from "./StaleEntryReview";
import { formatFactValue, formatIsoDay, unconvertedCurrency } from "./dealScreeningFormat";
import { questionsSectionName } from "./memoModel";

export interface GapsViewProps {
	run: DealScreeningRun;
	/** Resolve true when stored; the page reports failures. */
	onSetFact: (factKey: string, payload: DealScreeningSetFactPayload) => Promise<boolean>;
	onResolveConflict: (
		factKey: string,
		payload: DealScreeningResolveConflictPayload,
	) => Promise<boolean>;
	onOpenSource: (source: DealScreeningSource) => void;
	/** Back to the system's value for a fact (DOP-577). */
	onClearFact?: (factKey: string, payload: DealScreeningClearFactPayload) => Promise<boolean>;
	/** Make an earlier decision the active one again (DOP-577). */
	onRevertDecision?: (decisionId: string) => Promise<boolean>;
	/** A person's answer to open for editing on arrival (the memo's Edit link). */
	editTarget?: { key: string; period: string | null } | null;
	/** A conflict to open for choosing on arrival (a conflicted value in the memo). */
	conflictTarget?: { key: string; period: string | null } | null;
	/** Open the memo in edit mode on a figure's financial summary row (DOP-650). */
	onFillInSummary?: (factKey: string) => void;
	/** Open the memo in edit mode to write a required section (DOP-659). */
	onWriteSection?: (sectionKey: string) => void;
}

/** The phrase a person's "Accept" is recorded with. */
export const ACCEPTED_NOTE = "Accepted as not found in provided materials";

/**
 * What the right-hand side shows. `item` ties it to a row on the left; a
 * decision reopened from "Your decisions" has none. `null` falls back to the
 * first item a person can act on.
 */
type Panel =
	| { mode: "enter" | "choose" | "view"; item: ReviewItem }
	| {
			mode: "edit" | "rechoose" | "answer" | "review";
			fact: DealScreeningFact;
			item?: ReviewItem;
			/** The edit form was opened from the review of a stale entry (DOP-664). */
			fromReview?: boolean;
	  }
	| null;

const KIND_LABEL: Record<DealScreeningDecision["kind"], string> = {
	choose_source: "Chose a source",
	enter_value: "Entered a value",
	accept_na: "Accepted as not found",
	clear: "Cleared the decision",
};

/** "13.6 from Deck.pdf p.3", "8.0 — Intro call", "accepted as not found". */
function decisionSummary(decision: DealScreeningDecision): string {
	if (decision.kind === "choose_source") {
		const chosen = decision.candidates.find((c) => c.anchor === decision.chosen_anchor);
		const value = chosen ? formatFactValue(chosen.value, chosen.unit) : "a source";
		return chosen ? `${value} from ${chosen.filename}` : value;
	}
	if (decision.kind === "enter_value") {
		return `${formatFactValue(decision.value, decision.unit ?? "text")}${
			decision.note ? ` — ${decision.note}` : ""
		}`;
	}
	if (decision.kind === "accept_na") return "accepted as not found";
	return "back to the value read from the materials";
}

/** The deal's decisions grouped by slot, active first; a slot whose latest act is a clear is not shown as active. */
function decisionSlots(run: DealScreeningRun): {
	fact: DealScreeningFact | null;
	key: string;
	period: string | null;
	active: DealScreeningDecision | null;
	history: DealScreeningDecision[];
}[] {
	const slots = new Map<string, DealScreeningDecision[]>();
	for (const decision of run.decisions ?? []) {
		const id = `${decision.key}@${decision.period ?? ""}`;
		slots.set(id, [...(slots.get(id) ?? []), decision]);
	}
	// A run read before the ledger existed (or a fixture) carries entries on
	// its facts alone: they are decisions too, shown from the fact.
	for (const fact of personAnswers(run)) {
		const id = answerId(fact);
		if (slots.has(id)) continue;
		slots.set(id, [
			{
				id: "",
				key: fact.key,
				period: fact.period,
				kind: fact.state === "manual" ? "enter_value" : "accept_na",
				value: fact.value,
				unit: fact.unit,
				note: fact.note ?? "",
				chosen_anchor: null,
				chosen_attachment_id: null,
				candidates: [],
				recommended: null,
				created_by_user_id: fact.entered_by_user_id ?? "",
				created_by_name: fact.entered_by_name,
				created_at: fact.entered_at ?? "",
				active: true,
				superseded_by_id: null,
				origin_run_version: null,
			},
		]);
	}
	return Array.from(slots.entries()).map(([, decisions]) => {
		const active = decisions.find((d) => d.active) ?? null;
		const { key, period } = decisions[0];
		return {
			key,
			period,
			fact: run.facts.find((f) => f.key === key && f.period === period) ?? null,
			active: active && active.kind !== "clear" ? active : null,
			history: [...decisions].reverse(),
		};
	});
}

const answerId = (fact: DealScreeningFact): string => `${fact.key}@${fact.period ?? ""}`;

/** Every fact a person answered: a value entered, or not-found accepted. */
export function personAnswers(run: DealScreeningRun): DealScreeningFact[] {
	return run.facts.filter(
		(fact) =>
			fact.kind !== "derived" &&
			(fact.state === "manual" ||
				(fact.state === "not_available" && fact.entered_by_user_id !== null)),
	);
}

/** What selecting an item opens: its chooser, its card, or (omitted) its explanation. */
function panelFor(item: ReviewItem): Panel {
	if (item.kind === "omitted" || item.kind === "required") return { mode: "view", item };
	if (item.kind === "conflict") return { mode: "choose", item };
	if (item.kind === "stale") {
		const fact = item.facts[0];
		if (fact.state === "found") return { mode: "rechoose", fact, item };
		// An entered value the materials outgrew: keep it, take a reading, or
		// change it (DOP-664). An accepted "not found" has no value to keep.
		return fact.state === "manual" ? { mode: "review", fact, item } : { mode: "edit", fact, item };
	}
	return { mode: "enter", item };
}

/** The list's name for an item: the fact alone — the values are on the right. */
const shortTitle = (item: ReviewItem): string => {
	if (item.kind !== "conflict" && item.kind !== "stale") return item.title;
	const fact = item.facts[0];
	return fact.period ? `${fact.label} ${fact.period}` : fact.label;
};

/** The first item with something to answer: a gap to enter, else a conflict to choose. */
export function defaultPanel(items: ReviewItem[]): Panel {
	const item = items.find((candidate) => candidate.kind !== "omitted") ?? items[0];
	return item ? panelFor(item) : null;
}

/** Where the run looked for a missing fact, and what the company will be asked. */
const WhereDopplerLooked: React.FC<{ item: ReviewItem; questionsIn: string }> = ({
	item,
	questionsIn,
}) => {
	const reasons = Array.from(
		new Set(item.facts.map((fact) => fact.gap_reason).filter((r): r is string => Boolean(r))),
	);
	return (
		<aside aria-label="Where Doppler looked" className={`${CARD} w-full self-start px-4 py-3.5 lg:w-[320px] lg:shrink-0`}>
			<p className="flex items-center gap-2 text-sm font-semibold text-[#111111]">
				<Search className="h-4 w-4 text-[#004C4B]" aria-hidden />
				Where Doppler looked
			</p>
			{reasons.length > 0 ? (
				reasons.map((reason) => (
					<p key={reason} className="mt-1.5 text-[13px] leading-normal text-[#3f4247]">
						{reason}
					</p>
				))
			) : (
				<p className="mt-1.5 text-[13px] leading-normal text-[#3f4247]">
					Every file the run read.
				</p>
			)}
			{item.question && (
				<div className="mt-3">
					<p className="text-[12.5px] font-semibold text-[#3f4247]">The question in {questionsIn}</p>
					<p className="mt-1 text-[13px] leading-normal text-[#3f4247]">{item.question.text}</p>
				</div>
			)}
		</aside>
	);
};

const GapsView: React.FC<GapsViewProps> = ({
	run,
	onSetFact,
	onResolveConflict,
	onOpenSource,
	onClearFact,
	onRevertDecision,
	editTarget = null,
	conflictTarget = null,
	onFillInSummary,
	onWriteSection,
}) => {
	const items = reviewItems(run);
	const answers = personAnswers(run);
	const slots = decisionSlots(run).filter((slot) => slot.active || slot.history.length > 1);
	const [historyOpen, setHistoryOpen] = useState<string | null>(null);
	// Past decisions stay folded while there is something open to work on;
	// they open on their own when nothing is, or when one needs a second look.
	const [decisionsOpen, setDecisionsOpen] = useState<boolean>(
		() =>
			items.every((item) => item.kind === "omitted") ||
			editTarget !== null ||
			slots.some(
				(slot) =>
					slot.fact?.decision_status === "stale" || slot.fact?.decision_status === "orphaned",
			),
	);
	const questionsIn = questionsSectionName(run.sections);
	const [chosenPanel, setPanel] = useState<Panel>(() => {
		const target = editTarget
			? answers.find(
					(fact) => fact.key === editTarget.key && fact.period === editTarget.period,
				)
			: undefined;
		if (target) return { mode: "edit", fact: target };
		const conflict = conflictTarget
			? items.find(
					(item) =>
						item.kind === "conflict" &&
						item.facts[0].key === conflictTarget.key &&
						item.facts[0].period === conflictTarget.period,
				)
			: undefined;
		return conflict ? { mode: "choose", item: conflict } : null;
	});
	// A selection whose item a save has since resolved falls through to the
	// next open one, so the tab never shows an answered item.
	const stillOpen =
		chosenPanel === null ||
		!("item" in chosenPanel) ||
		chosenPanel.item === undefined ||
		items.some((item) => item.id === chosenPanel.item?.id);
	const panel = chosenPanel !== null && stillOpen ? chosenPanel : defaultPanel(items);
	const selectedId = panel && "item" in panel ? (panel.item?.id ?? null) : null;
	const position = selectedId ? items.findIndex((item) => item.id === selectedId) : -1;
	const decisionsShown =
		decisionsOpen || (panel !== null && "fact" in panel && panel.item === undefined);
	const [busyId, setBusyId] = useState<string | null>(null);

	const busy = (id: string, write: () => Promise<boolean>) => async () => {
		setBusyId(id);
		try {
			if (await write()) setPanel(null);
		} finally {
			setBusyId(null);
		}
	};

	/** Step to the item before or after the one on screen, without recording anything. */
	const step = (by: number) => {
		const target = items[position + by];
		if (target) setPanel(panelFor(target));
	};

	const accept = (id: string) => (fact: DealScreeningFact) =>
		// One member accepted is enough: a block group is satisfied by any one.
		void busy(id, () =>
			onSetFact(fact.key, { accept_na: true, note: ACCEPTED_NOTE, period: fact.period }),
		)();

	const saveEntry = (id: string) => (fact: DealScreeningFact, payload: DealScreeningSetFactPayload) =>
		void busy(id, () => onSetFact(fact.key, payload))();

	const chooseFor = (id: string, fact: DealScreeningFact) => (chosen: DealScreeningSource | null) =>
		void busy(id, () =>
			onResolveConflict(fact.key, {
				chosen_attachment_id: chosen?.attachment_id ?? null,
				chosen_anchor: chosen?.anchor ?? null,
				period: fact.period,
			}),
		)();

	const clear = (fact: DealScreeningFact) =>
		onClearFact
			? busy(answerId(fact), () => onClearFact(fact.key, { period: fact.period }))()
			: Promise.resolve();
	/**
	 * Undo: back to what was in force before the current decision — the
	 * decision before it when there was one, else the value read from the
	 * materials. The history keeps every step, so undo is itself undoable.
	 */
	const undo = (slot: (typeof slots)[number]) => {
		if (!slot.fact || !slot.active) return Promise.resolve();
		const previous = slot.history.find((d) => d.id !== slot.active?.id && d.id !== "");
		if (previous && previous.kind !== "clear" && onRevertDecision) {
			return busy(answerId(slot.fact), () => onRevertDecision(previous.id))();
		}
		return clear(slot.fact);
	};
	const revert = (decision: DealScreeningDecision) =>
		onRevertDecision
			? busy(`revert:${decision.id}`, () => onRevertDecision(decision.id))()
			: Promise.resolve();

	/**
	 * What "Change" opens for a slot: the chooser for a chosen source, the
	 * card prefilled for an entry, and — on a cleared slot — the chooser when
	 * the materials hold several readings, else a blank card to enter one.
	 */
	const change = (slot: (typeof slots)[number]) => {
		if (!slot.fact) return;
		if (slot.active?.kind === "choose_source") setPanel({ mode: "rechoose", fact: slot.fact });
		else if (slot.active) setPanel({ mode: "edit", fact: slot.fact });
		else if (slot.fact.alternates.length > 0) setPanel({ mode: "rechoose", fact: slot.fact });
		else setPanel({ mode: "answer", fact: slot.fact });
	};

	/** Leave a reopened decision: back to the open items. */
	const close = () => setPanel(null);

	const detail = (): React.ReactNode => {
		if (!panel) return null;
		if (panel.mode === "enter") {
			const { item } = panel;
			const eachYear =
				onFillInSummary && item.facts.length === 1 && needsEachYear(run, item.facts[0])
					? () => onFillInSummary(item.facts[0].key)
					: undefined;
			return (
				<div className="flex flex-col gap-6 lg:flex-row lg:items-start">
					<div className="min-w-0 flex-1 lg:max-w-[560px]">
						<EnterWithSourceCard
							questionsIn={questionsIn}
							key={item.id}
							facts={item.facts}
							blocking={item.kind === "blocks"}
							orAnswers={item.alsoAnswers}
							saving={busyId === item.id}
							onSave={saveEntry(item.id)}
							onAccept={accept(item.id)}
							onFillEachYear={eachYear}
						/>
					</div>
					<WhereDopplerLooked item={item} questionsIn={questionsIn} />
				</div>
			);
		}
		if (panel.mode === "choose") {
			const { item } = panel;
			return (
				<ConflictChooser
					questionsIn={questionsIn}
					key={item.id}
					fact={item.facts[0]}
					saving={busyId === item.id}
					onChoose={chooseFor(item.id, item.facts[0])}
					onEnter={saveEntry(item.id)}
					onOpenSource={onOpenSource}
				/>
			);
		}
		if (panel.mode === "view") {
			const { item } = panel;
			if (item.kind === "required") {
				// Set to appear in every memo and left out (DOP-657): a person
				// writes it, or the memo is generated again. It blocks review.
				const sectionKey = item.section?.key;
				return (
					<div className="flex max-w-[640px] flex-col gap-4" data-testid="required-section">
						<div>
							<p className={`${KICKER} ${KIND_KICKER.required.color}`}>{KIND_KICKER.required.label}</p>
							<h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#111111]">
								{item.title}
							</h2>
							<p className="mt-1 text-sm text-[#5B6472]">
								{item.section?.status === "omitted"
									? `This section is set to appear in every memo, and Doppler could not write it${
											item.detail ? `: ${item.detail.replace(/\.\s*$/, "")}` : ""
										}.`
									: `This section is set to appear in every memo, and it is empty: ${(
											item.detail ?? ""
										).toLowerCase()}.`}{" "}
								The memo cannot be marked reviewed until it is written.
							</p>
						</div>
						<div className={`${CARD} divide-y divide-[#f1f2f4]`}>
							<p className="px-4 py-3 text-sm font-semibold text-[#111111]">What you can do</p>
							<div className="flex gap-3 px-4 py-3.5">
								<CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]" aria-hidden />
								<div>
									<p className="text-sm font-semibold text-[#111111]">Write it yourself</p>
									<p className="mt-0.5 text-[13px] leading-normal text-[#5B6472]">
										Open the memo in edit mode, write the section and save a new version.
									</p>
									{onWriteSection && sectionKey && (
										<button
											type="button"
											className={`${BTN_SM} mt-2`}
											onClick={() => onWriteSection(sectionKey)}
										>
											Write it in edit mode
										</button>
									)}
								</div>
							</div>
							<div className="flex gap-3 px-4 py-3.5">
								<FilePlus className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]" aria-hidden />
								<div>
									<p className="text-sm font-semibold text-[#111111]">Have material that covers it?</p>
									<p className="mt-0.5 text-[13px] leading-normal text-[#5B6472]">
										Add it to the deal's files and run the memo again.
									</p>
								</div>
							</div>
						</div>
					</div>
				);
			}
			return (
				<div className="flex max-w-[640px] flex-col gap-4">
					<div>
						<p className={`${KICKER} ${KIND_KICKER.omitted.color}`}>{KIND_KICKER.omitted.label}</p>
						<h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#111111]">
							{item.title}
						</h2>
						{item.detail && <p className="mt-1 text-sm text-[#5B6472]">{item.detail}</p>}
					</div>
					<div className={`${CARD} divide-y divide-[#f1f2f4]`} data-testid="omitted-options">
						<p className="px-4 py-3 text-sm font-semibold text-[#111111]">What you can do</p>
						<div className="flex gap-3 px-4 py-3.5">
							<FilePlus className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]" aria-hidden />
							<div>
								<p className="text-sm font-semibold text-[#111111]">Have material that covers it?</p>
								<p className="mt-0.5 text-[13px] leading-normal text-[#5B6472]">
									Add it to the deal's files and run the memo again. Doppler writes this section
									from what you add, with every sentence sourced.
								</p>
							</div>
						</div>
						<div className="flex gap-3 px-4 py-3.5">
							<CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]" aria-hidden />
							<div>
								<p className="text-sm font-semibold text-[#111111]">Happy to leave it out?</p>
								<p className="mt-0.5 text-[13px] leading-normal text-[#5B6472]">
									Nothing to do. The memo says the section was left out and why
									{item.question ? `, and ${questionsIn} asks the company: “${item.question.text}”` : ""}.
									Doppler never fills a section from general knowledge.
								</p>
							</div>
						</div>
					</div>
				</div>
			);
		}
		if (!("fact" in panel)) return null;
		const id = answerId(panel.fact);
		const staleNote = panel.item?.kind === "stale" && panel.item.detail && (
			<div className="mb-4">
				<p className={`${KICKER} ${KIND_KICKER.stale.color}`}>{KIND_KICKER.stale.label}</p>
				<p className="mt-1.5 text-sm text-[#5B6472]">{panel.item.detail}</p>
			</div>
		);
		// Opened from a row (a stale item), Previous / Next are the way out;
		// reopened from "Your decisions", Cancel goes back to the open items.
		const leave = panel.item ? undefined : close;
		const leaveLabel = "Cancel";
		if (panel.mode === "review") {
			return (
				<div>
					<StaleEntryReview
						key={`review:${id}`}
						fact={panel.fact}
						saving={busyId === id}
						onKeep={saveEntry(id)}
						onChoose={(chosen) => chooseFor(id, panel.fact)(chosen)}
						onOpenSource={onOpenSource}
						onChange={() =>
							setPanel({ mode: "edit", fact: panel.fact, item: panel.item, fromReview: true })
						}
					/>
				</div>
			);
		}
		if (panel.mode === "rechoose") {
			return (
				<div>
					{staleNote}
					<ConflictChooser
						questionsIn={questionsIn}
						key={`rechoose:${id}`}
						fact={panel.fact}
						saving={busyId === id}
						onChoose={chooseFor(id, panel.fact)}
						onEnter={saveEntry(id)}
						onOpenSource={onOpenSource}
						onCancel={leave}
						cancelLabel={leaveLabel}
					/>
				</div>
			);
		}
		// "Change it" from the review: the reason was read there, and Back
		// returns to the three answers rather than leaving only "Skip to next".
		const backToReview = panel.fromReview
			? () => setPanel({ mode: "review", fact: panel.fact, item: panel.item })
			: undefined;
		return (
			<div className="max-w-[560px]">
				{!panel.fromReview && staleNote}
				<EnterWithSourceCard
					questionsIn={questionsIn}
					key={`${panel.mode}:${id}`}
					facts={[panel.fact]}
					blocking={false}
					editing={panel.mode === "edit"}
					saving={busyId === id}
					onSave={saveEntry(id)}
					onCancel={backToReview ?? leave}
					cancelLabel={backToReview ? "Back" : leaveLabel}
				/>
			</div>
		);
	};

	const nothingOpen = (
		<div className={`${CARD} flex items-center gap-3 px-5 py-5 text-sm text-[#3f4247]`}>
			<CircleCheck className="h-5 w-5 shrink-0 text-[#067647]" aria-hidden />
			Nothing on this memo needs a person.
		</div>
	);

	if (items.length === 0 && answers.length === 0 && slots.length === 0) {
		return <div className="mx-auto max-w-[1280px]">{nothingOpen}</div>;
	}

	return (
		<div className="mx-auto flex max-w-[1280px] flex-col gap-6">
			{items.length === 0 ? (
				<>
					{nothingOpen}
					{panel && <div>{detail()}</div>}
				</>
			) : (
				<div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
					<nav aria-label="Open gaps" className={`${CARD} p-3 lg:sticky lg:top-0`}>
						<div className="px-2 pb-3 pt-1">
							<div className="flex items-baseline justify-between gap-2">
								<p className="text-sm font-semibold text-[#111111]">
									{items.length} {items.length === 1 ? "item needs" : "items need"} you
								</p>
							</div>
							<p className="mt-0.5 text-[12.5px] text-[#6B7280]">
								Work through them in order, or click any one.
							</p>
						</div>
						<ol className="flex flex-col gap-0.5">
							{items.map((item, index) => {
								const selected = item.id === selectedId;
								const kicker = KIND_KICKER[item.kind];
								return (
									<li key={item.id}>
										<button
											type="button"
											data-testid={`gap-row-${item.id}`}
											aria-current={selected ? "true" : undefined}
											onClick={() => setPanel(panelFor(item))}
											className={`flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${
												selected ? "bg-[#E6F3F3]" : "hover:bg-[#f7f7f6]"
											}`}
										>
											<span
												className={`mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
													selected ? "bg-[#004C4B] text-white" : "border border-[#d1d5db] text-[#5B6472]"
												}`}
												aria-hidden
											>
												{index + 1}
											</span>
											<span className="flex min-w-0 flex-col gap-0.5">
												<span
													className={`truncate text-[13.5px] ${
														selected ? "font-semibold text-[#004C4B]" : "font-medium text-[#1F2937]"
													}`}
												>
													{shortTitle(item)}
												</span>
												<span className={`text-xs ${kicker.color}`}>
													{item.kind === "stale" ? "Review choice" : kicker.label}
												</span>
											</span>
										</button>
									</li>
								);
							})}
						</ol>
					</nav>
					<div className="min-w-0">
						{detail()}
						{position >= 0 && (
							<div
								className="mt-5 flex flex-wrap items-center gap-3 border-t border-[#e5e7eb] pt-4"
								data-testid="gap-footer"
							>
								<button
									type="button"
									className={`${BTN} h-10 px-4`}
									disabled={position === 0}
									onClick={() => step(-1)}
								>
									<ChevronLeft className="h-4 w-4" aria-hidden />
									Previous
								</button>
								<p className="flex-1 text-center text-[12.5px] text-[#6B7280]">
									<span className="font-semibold text-[#3f4247]" data-testid="gap-position">
										Item {position + 1} of {items.length}
									</span>
									{" · "}whatever you decide is kept when the memo is re-run
								</p>
								<button
									type="button"
									className={`${BTN} h-10 px-4`}
									disabled={position === items.length - 1}
									onClick={() => step(1)}
								>
									Skip to next
									<ChevronRight className="h-4 w-4" aria-hidden />
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{slots.length > 0 && (
				<section aria-label="Your decisions" data-testid="your-decisions" className={CARD}>
					<h3>
						<button
							type="button"
							onClick={() => setDecisionsOpen((open) => !open)}
							aria-expanded={decisionsShown}
							className="flex w-full items-center gap-2 px-5 py-3 text-left"
						>
							<ChevronRight
								className={`h-4 w-4 text-[#6B7280] transition-transform ${decisionsShown ? "rotate-90" : ""}`}
								aria-hidden
							/>
							<span className="text-sm font-semibold text-[#111111]">Your decisions</span>
							<span className="text-[13px] text-[#6B7280]">{slots.length}</span>
							<span className="ml-auto text-[12.5px] text-[#6B7280]">
								Kept when the memo is re-run; change or undo any of them
							</span>
						</button>
					</h3>
					{decisionsShown && (
					<ul className="border-t border-[#eef0f2]">
						{slots.map((slot) => {
							const id = `${slot.key}@${slot.period ?? ""}`;
							const fact = slot.fact;
							const status = fact?.decision_status ?? null;
							const editing =
								panel !== null && "fact" in panel && answerId(panel.fact) === id;
							return (
								<li
									key={id}
									className={`border-b border-[#f1f2f4] px-5 py-3 last:border-0 ${
										editing ? "bg-[#E6F3F3]/50" : ""
									}`}
									data-testid={`decision-row-${id}`}
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="text-sm font-semibold text-[#111111]">
												{fact?.label ?? slot.key}
												{slot.period ? ` · ${slot.period}` : ""}
												{status === "stale" && (
													<span className="ml-2 rounded-full bg-[#fffaeb] px-2 py-0.5 text-[11px] font-semibold text-[#b54708]">
														review
													</span>
												)}
												{status === "orphaned" && (
													<span className="ml-2 rounded-full bg-[#fffaeb] px-2 py-0.5 text-[11px] font-semibold text-[#b54708]">
														source gone
													</span>
												)}
											</p>
											{slot.active ? (
												<p className="mt-0.5 text-sm text-[#3f4247]">
													{KIND_LABEL[slot.active.kind]}: {decisionSummary(slot.active)}
												</p>
											) : (
												<p className="mt-0.5 text-sm text-[#6B7280]">
													{slot.history[0]?.kind === "clear"
														? `Cleared by ${slot.history[0].created_by_name ?? "a person"}${
																slot.history[0].created_at
																	? `, ${formatIsoDay(slot.history[0].created_at)}`
																	: ""
															} · the memo reads from the materials`
														: "The memo reads from the materials"}
													{fact
														? `: ${
																fact.state === "found" || fact.state === "derived"
																	? formatFactValue(fact.value, fact.unit, unconvertedCurrency(fact))
																	: "not found"
															}`
														: ""}
												</p>
											)}
											{slot.active && (
												<p className="mt-0.5 text-xs text-[#6B7280]">
													{slot.active.created_by_name ? `${slot.active.created_by_name}, ` : ""}
													{slot.active.created_at ? formatIsoDay(slot.active.created_at) : ""}
													{slot.active.origin_run_version !== null
														? ` · on v${slot.active.origin_run_version}`
														: ""}
												</p>
											)}
											{fact?.stale_reason && (
												<p className="mt-0.5 text-xs text-[#b54708]">{fact.stale_reason}</p>
											)}
										</div>
										<div className="flex shrink-0 flex-wrap justify-end gap-1.5">
											{fact && slot.active && (
												<button
													type="button"
													className={BTN_SM}
													disabled={busyId !== null || run.generating}
													onClick={() => change(slot)}
												>
													Change
												</button>
											)}
											{slot.active && fact && onClearFact && (
												<button
													type="button"
													className={BTN_SM}
													disabled={busyId !== null || run.generating}
													onClick={() => void undo(slot)}
													title="Back to your previous decision, or to the value read from the materials; every step stays in the history"
												>
													Undo
												</button>
											)}
											{slot.history.length > 1 && (
												<button
													type="button"
													className={BTN_SM}
													onClick={() => setHistoryOpen((open) => (open === id ? null : id))}
													aria-expanded={historyOpen === id}
												>
													{historyOpen === id ? "Hide history" : `History (${slot.history.length})`}
												</button>
											)}
										</div>
									</div>
									{historyOpen === id && (
										<ol
											className="mt-2.5 space-y-1.5 border-l-2 border-[#eef0f2] pl-3"
											data-testid={`decision-history-${id}`}
										>
											{slot.history.map((decision) => (
												<li key={decision.id} className="flex items-start justify-between gap-3 text-xs leading-4">
													<span className={decision.active ? "text-[#111111]" : "text-[#6B7280]"}>
														{KIND_LABEL[decision.kind]}: {decisionSummary(decision)}
														{" — "}
														{decision.created_by_name ? `${decision.created_by_name}, ` : ""}
														{formatIsoDay(decision.created_at)}
														{decision.origin_run_version !== null ? ` (v${decision.origin_run_version})` : ""}
														{decision.active ? " · current" : ""}
													</span>
													{!decision.active && decision.kind !== "clear" && onRevertDecision && (
														<button
															type="button"
															className="shrink-0 font-medium text-[#004C4B] underline underline-offset-2 disabled:opacity-50"
															disabled={busyId !== null || run.generating}
															onClick={() => void revert(decision)}
														>
															Revert to this
														</button>
													)}
												</li>
											))}
										</ol>
									)}
								</li>
							);
						})}
					</ul>
					)}
				</section>
			)}
		</div>
	);
};

export default GapsView;
